import type { ClassInvite, ClassInvitePreviewDto, CreateInviteRequest, ResolvedClassInvite } from '../types/class';
import type {
	AcceptClassInviteResponseDto,
	CreateClassInviteResponseDto,
	VerifyClassInviteResponseDto,
} from '../types/api/class';
import {
	acceptClassInvite as acceptClassInviteApi,
	createClassInvite,
	getActiveClassInvite,
	verifyClassInvite,
} from './classService';
import i18n from '@/i18n';
import { getApiLanguageCode } from '@/lib/i18nLocale';

/**
 * 붙여넣은 문자열에서 초대 토큰만 추출합니다.
 * 예: `https://host/class/invite/abc123` → `abc123`
 */
export function parseInviteTokenFromInput(raw: string): string {
	const t = raw.trim();
	if (!t) return '';
	const m = t.match(/\/class\/invite\/([^/?#]+)/i);
	if (m?.[1]) {
		try {
			return decodeURIComponent(m[1]);
		} catch {
			return m[1];
		}
	}
	return t.replace(/\s+/g, '');
}

function toPreviewDto(v: VerifyClassInviteResponseDto): ClassInvitePreviewDto {
	return {
		classId: v.classId,
		title: v.title,
		thumbnailImageUrl: v.thumbnailImageUrl ?? null,
		expiresAt: v.expiresAt ?? null,
		participantCount: v.participantCount,
		maxParticipants: v.maxParticipants ?? null,
		isAlreadyJoined: v.isAlreadyJoined,
	};
}

function farFutureIso(): string {
	const d = new Date();
	d.setFullYear(d.getFullYear() + 50);
	return d.toISOString();
}

function buildClassInvite(token: string, p: VerifyClassInviteResponseDto): ClassInvite {
	const expiresAt =
		p.expiresAt != null && String(p.expiresAt).trim() !== '' ? String(p.expiresAt) : farFutureIso();
	return {
		id: `invite-${token}`,
		classId: String(p.classId),
		token,
		inviteCode: token,
		expiresAt,
		currentUses: 0,
		createdBy: '',
		createdAt: new Date().toISOString(),
		status: 'active',
	};
}

function mapCreateDtoToClassInvite(classIdStr: string, dto: CreateClassInviteResponseDto): ClassInvite {
	const expiresAt =
		dto.expiresAt != null && String(dto.expiresAt).trim() !== '' ? String(dto.expiresAt) : farFutureIso();
	const short =
		typeof dto.inviteCode === 'string' && dto.inviteCode.trim().length > 0 ? dto.inviteCode.trim() : null;
	const tokenStr = dto.token != null && String(dto.token).trim() !== '' ? String(dto.token).trim() : '';
	return {
		id: tokenStr ? `invite-${tokenStr}` : `invite-${classIdStr}-${Date.now()}`,
		classId: classIdStr,
		token: tokenStr,
		inviteCode: short ?? tokenStr,
		expiresAt,
		currentUses: 0,
		createdBy: '',
		createdAt: new Date().toISOString(),
		status: 'active',
	};
}

/**
 * 클래스 초대 — Swagger: POST/GET /class/.../invite, GET verify, POST accept
 */
export const classInviteService = {
	createInvite: async (request: CreateInviteRequest): Promise<ClassInvite> => {
		const numericId = Number.parseInt(String(request.classId), 10);
		if (!Number.isFinite(numericId) || numericId <= 0) {
			throw new Error('유효하지 않은 클래스 ID입니다.');
		}
		const body: { expiresAt?: string } = {};
		if (request.expirationDays != null && request.expirationDays > 0) {
			const d = new Date();
			d.setDate(d.getDate() + request.expirationDays);
			d.setHours(23, 59, 59, 999);
			body.expiresAt = d.toISOString();
		}
		const dto = await createClassInvite(numericId, body);
		return mapCreateDtoToClassInvite(String(request.classId), dto);
	},

	/** 서버에 저장된 활성 초대 링크 조회 (관리자) */
	loadActiveInvite: async (classId: string): Promise<ClassInvite | null> => {
		const numericId = Number.parseInt(String(classId), 10);
		if (!Number.isFinite(numericId) || numericId <= 0) return null;
		try {
			const dto = await getActiveClassInvite(numericId);
			const invite = mapCreateDtoToClassInvite(String(classId), dto);
			// token이 없는 유효하지 않은 초대는 무시
			if (!invite.token || invite.token.trim() === '') {
				return null;
			}
			return invite;
		} catch (error) {
			// 404: 활성 초대가 없음 (정상 케이스)
			// 다른 에러도 null 반환하여 초대 생성 폼 표시
			return null;
		}
	},

	getInviteByToken: async (token: string): Promise<ResolvedClassInvite> => {
		const trimmed = token.trim();
		if (!trimmed) {
			throw new Error('초대 토큰이 비어 있습니다.');
		}
		const verify = await verifyClassInvite(trimmed, {
			language: getApiLanguageCode(i18n.language),
		});
		return {
			invite: buildClassInvite(trimmed, verify),
			preview: toPreviewDto(verify),
		};
	},

	getInviteByCode: async (codeOrLink: string): Promise<ResolvedClassInvite | null> => {
		const token = parseInviteTokenFromInput(codeOrLink);
		if (!token) return null;
		try {
			return await classInviteService.getInviteByToken(token);
		} catch {
			return null;
		}
	},

	acceptInvite: async (token: string): Promise<AcceptClassInviteResponseDto> => {
		return acceptClassInviteApi(token.trim());
	},
};
